import {
  ON_CONVERSATION_UNENDED_EVENT_TYPE,
  ON_SESSION_CUSTOM_ATTRIBUTES_UPDATED_EVENT_TYPE,
  ON_SESSION_TITLE_CHANGED_EVENT_TYPE,
} from 'core/constants';
import { runListenersForEvent } from 'core_main/globals/events';
import { createEndConversationCallbackResponse } from 'core_main/sdk/end_conversation/helpers';
import {
  areConversationCustomAttributesEqual,
  normalizeSessionCustomAttributes,
} from 'core_main/sdk/chat_history_status_filter';
import { addSessionToStore, getStore } from 'core_main/store';
// eslint-disable-next-line import/no-cycle
import { rekeyRealtimeToCustomer } from 'core_main/pubnub/helpers/rekeyRealtime';
import { PubNubSessionUpdateMessageEvent } from '../types';

const INITIAL_DELAY_MS = 400; // Delay before re-keying realtime after a customer merge

// Store timeout ID to prevent accumulation of timers from multiple customer merge events
let initialTimeoutId: ReturnType<typeof setTimeout> | null = null;

const hasSessionCustomAttributesChanged = (
  before: PubNubSessionUpdateMessageEvent['data']['before'],
  after: PubNubSessionUpdateMessageEvent['data']['after'],
) => {
  const beforeCustom = normalizeSessionCustomAttributes(
    before?.attributes?.custom,
  );
  const afterCustom = normalizeSessionCustomAttributes(
    after?.attributes?.custom,
  );

  return !areConversationCustomAttributesEqual(beforeCustom, afterCustom);
};

export const handleChatSessionUpdateEvent = (
  message: PubNubSessionUpdateMessageEvent,
) => {
  const lockedAtOp = message?.data?.changes?.attributes?.lockedAt?.op;

  const before = message?.data?.before;
  const after = message?.data?.after;

  const beforeCustomerId = before?.relationships?.customer?.data?.id;
  const afterCustomerId = after?.relationships?.customer?.data?.id;

  if (
    beforeCustomerId &&
    beforeCustomerId !== afterCustomerId &&
    afterCustomerId
  ) {
    // Clear any existing initial timeout to prevent accumulation
    if (initialTimeoutId !== null) {
      clearTimeout(initialTimeoutId);
      initialTimeoutId = null;
    }

    initialTimeoutId = setTimeout(() => {
      initialTimeoutId = null;
      rekeyRealtimeToCustomer(afterCustomerId, {
        source: 'event',
        fromCustomerId: beforeCustomerId,
      });
    }, INITIAL_DELAY_MS);
  }

  if (after?.id && after?.attributes) {
    const useLegacy = getStore().useLegacyConversationPreview === true;
    const sessionToStore = useLegacy
      ? { ...after, attributes: { ...after.attributes, name: undefined } }
      : after;
    addSessionToStore(sessionToStore);
    const previewDetailsNameBefore = (
      typeof before?.attributes?.previewDetails?.name === 'string'
        ? before.attributes.previewDetails.name
        : ''
    ).trim();
    const previewDetailsNameAfter = (
      typeof after.attributes?.previewDetails?.name === 'string'
        ? after.attributes.previewDetails.name
        : ''
    ).trim();
    const sessionTitleChanged =
      before?.attributes?.name !== after.attributes.name ||
      previewDetailsNameBefore !== previewDetailsNameAfter;
    if (!useLegacy && sessionTitleChanged) {
      runListenersForEvent(ON_SESSION_TITLE_CHANGED_EVENT_TYPE, after);
    }
    if (hasSessionCustomAttributesChanged(before, after)) {
      runListenersForEvent(
        ON_SESSION_CUSTOM_ATTRIBUTES_UPDATED_EVENT_TYPE,
        after,
      );
    }
  }

  if (!lockedAtOp) return;

  if (lockedAtOp === 'remove') {
    const callbackResponse = createEndConversationCallbackResponse(after);

    runListenersForEvent(ON_CONVERSATION_UNENDED_EVENT_TYPE, callbackResponse);
  }
};
